import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Coins, Zap, Crown } from 'lucide-react';
import { toast } from 'sonner';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import PaymentMethodModal from '../components/PaymentMethodModal';
import { useCredits } from '../lib/hooks';
import { usePricing } from '../hooks/usePricing';

const Credits = ({ profile, setProfile }) => {
  const { credits, loading, refetch } = useCredits(profile);
  const { pricing, loading: pricingLoading } = usePricing();
  const [purchasing, setPurchasing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  // Check for payment success/failure in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');

    if (status === 'success') {
      toast.success('Payment successful! Your credits will be added shortly.');
      refetch(); // Refresh credits
      // Clean up URL
      window.history.replaceState({}, '', '/credits');
    } else if (status === 'failed') {
      toast.error('Payment failed. Please try again.');
      window.history.replaceState({}, '', '/credits');
    }
  }, [refetch]);

  const packages = [
    {
      id: 'pack50',
      name: 'Starter',
      price: pricing.pack50_price || 299,
      credits: 50,
      popular: false,
      icon: Coins
    },
    {
      id: 'pack100',
      name: 'Popular',
      price: pricing.pack100_price || 499,
      credits: 100,
      popular: true,
      icon: Zap
    },
    {
      id: 'pack300',
      name: 'Pro',
      price: pricing.pack300_price || 999,
      credits: 300,
      popular: false,
      icon: Crown,
      bonus: '+50 bonus credits'
    }
  ];

  // Use dynamic pricing from admin settings (ensure pricing is loaded)
  const creditCosts = [
    { action: 'Post Featured Gig (top of list, 7 days)', cost: pricing?.featured_post || 30, icon: '⭐' },
    { action: 'Boost existing gig to top', cost: pricing?.boost_post || 15, icon: '🚀' },
    { action: 'View applicant profiles', cost: 0, icon: '👤' },
    { action: 'Post normal job/gig', cost: 0, icon: '✅' }
  ];

  const handlePurchase = async (packId) => {
    // Find the package details
    const pkg = packages.find(p => p.id === packId);
    if (!pkg) return;

    // Show payment method selection modal
    setSelectedPackage(pkg);
    setShowPaymentModal(true);
  };

  const handleConfirmPayment = async (paymentData) => {
    setPurchasing(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Please log in');
        return;
      }

      const response = await api.post(
        '/api/payments/create-invoice',
        {
          pack: selectedPackage.id,
          payment_channel: paymentData.paymentChannel
        },
        {
          headers: {
            Authorization: `Bearer ${session.session.access_token}`
          }
        }
      );

      if (response.data.invoice_url) {
        window.location.href = response.data.invoice_url;
      } else {
        toast.error('Failed to create payment invoice');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create payment');
    } finally {
      setPurchasing(false);
      setShowPaymentModal(false);
    }
  };

  return (
    <div className="min-h-screen-safe pb-nav md:pb-0">
      <TopBar profile={profile} setProfile={setProfile} />

      <div className="px-4 py-8 max-w-7xl mx-auto">
        {/* Current Balance */}
        <Card className="mb-8 bg-gradient-to-r from-primary to-primary/70 text-white border-none">
          <CardContent className="p-8 text-center">
            <Coins size={64} className="mx-auto mb-4" />
            {loading ? (
              <div className="animate-pulse">
                <div className="h-16 w-32 bg-white/30 rounded mx-auto mb-2" />
              </div>
            ) : (
              <>
                <h2 className="text-5xl sm:text-6xl font-bold mb-2">
                  {credits.balance}
                </h2>
                <p className="text-xl opacity-90">
                  Credits Available
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Credit Usage Guide */}
        <div className="mb-8">
          <h2 className="font-heading text-2xl font-bold mb-4">How Credits Work</h2>
          <Card>
            <CardContent className="p-6">
              <div className="space-y-3">
                {creditCosts.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{item.icon}</span>
                      <span className="text-sm md:text-base">{item.action}</span>
                    </div>
                    <Badge variant={item.cost === 0 ? 'secondary' : 'default'} className="text-sm">
                      {item.cost === 0 ? 'FREE' : `${item.cost} credits`}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Packages */}
        <div className="mb-8">
          <h2 className="font-heading text-2xl font-bold mb-4">Purchase Credits</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {packages.map((pack) => {
              const Icon = pack.icon;
              return (
                <Card
                  key={pack.id}
                  className={`relative overflow-hidden transition-all hover:shadow-xl ${
                    pack.popular ? 'border-primary border-2 shadow-lg' : ''
                  }`}
                >
                  {pack.popular && (
                    <Badge className="absolute top-4 right-4 bg-primary text-white">
                      Most Popular
                    </Badge>
                  )}
                  <CardContent className="p-6">
                    <div className="text-center mb-6">
                      <Icon size={48} className="mx-auto mb-4 text-primary" />
                      <h3 className="font-heading text-2xl font-bold mb-2">
                        {pack.name}
                      </h3>
                      <div className="mb-2">
                        <span className="text-4xl font-bold">₱{pack.price}</span>
                      </div>
                      <p className="text-lg font-semibold text-primary">{pack.credits} Credits</p>
                      {pack.bonus && (
                        <p className="text-sm text-green-600 font-medium">{pack.bonus}</p>
                      )}
                    </div>

                    <Button
                      className="w-full rounded-xl"
                      size="lg"
                      onClick={() => handlePurchase(pack.id)}
                      disabled={purchasing}
                      variant={pack.popular ? 'default' : 'outline'}
                    >
                      {purchasing ? 'Processing...' : 'Purchase Now'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Worker Note */}
        {profile?.role === 'job_seeker' || profile?.role === 'contractor' ? (
          <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
            <CardContent className="p-6 text-center">
              <h3 className="font-heading text-xl font-bold mb-2 text-green-800 dark:text-green-200">
                Everything is FREE!
              </h3>
              <p className="text-green-700 dark:text-green-300">
                Credits are for employers and project owners who want to feature or boost their posts.
                As a worker, everything is 100% FREE forever - apply to unlimited jobs and gigs!
              </p>
            </CardContent>
          </Card>
        ) : null}

        {/* Payment Methods */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p className="mb-2">Secure payments powered by PayMongo</p>
          <p>Pay using QR Ph - scan with GCash, Maya, BDO, BPI, UnionBank, or any bank app</p>
        </div>
      </div>

      {/* Payment Method Selection Modal */}
      {selectedPackage && (
        <PaymentMethodModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          baseAmount={selectedPackage.price}
          packageName={`${selectedPackage.name} Pack - ${selectedPackage.credits} Credits`}
          onConfirm={handleConfirmPayment}
        />
      )}

      <BottomNav profile={profile} />
    </div>
  );
};

export default Credits;
